import { supabase } from '@/lib/supabase';

export interface Lead {
  id: string;
  email: string;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface CreateLeadInput {
  email: string;
  source?: string;
}

export interface LeadServiceResponse {
  data: null;
  error: string | null;
}

/**
 * Sends welcome email to new lead via Supabase Edge Function
 */
async function sendWelcomeEmail(email: string): Promise<void> {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Supabase configuration missing');
      return;
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/send-welcome-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ email }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Failed to send welcome email:', errorData);
    }
  } catch (error) {
    console.error('Error sending welcome email:', error);
  }
}

/**
 * Creates a new lead in the database (INSERT ONLY — safest option)
 */
export async function createLead(input: CreateLeadInput): Promise<LeadServiceResponse> {
  try {
    const { error } = await supabase
      .from('leads')
      .insert({
        email: input.email,
        source: input.source || 'landing_page'
      });

    if (error) {
      // Handle unique constraint (duplicate email)
      if (error.code === '23505') {
        return {
          data: null,
          error: 'This email is already registered for early access'
        };
      }

      return {
        data: null,
        error: error.message
      };
    }

    // Send welcome email asynchronously (don't block user)
    sendWelcomeEmail(input.email).catch(err => {
      console.error('Background email send failed:', err);
    });

    // Secure mode: we don't return any lead data
    return {
      data: null,
      error: null
    };
  } catch (error: any) {
    if (
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('NetworkError') ||
      (error?.name === 'TypeError' && error?.message?.includes('fetch'))
    ) {
      return {
        data: null,
        error:
          'Cannot connect to database. Your Supabase project may be paused or deleted.'
      };
    }

    return {
      data: null,
      error: 'An unexpected error occurred. Please try again.'
    };
  }
}

/**
 * Get all leads (admin-only — disabled for now for security)
 * Keep this commented out until you add admin auth.
 */
/*
export async function getAllLeads() {
  return { data: null, error: "SELECT is disabled for public users." };
}
*/